import React, { useRef, useState } from 'react';
import { usePreview } from '../contexts/PreviewContext';
import { Button } from './ui/button';
import { 
  ShoppingBag, 
  X, 
  Play, 
  Square,
  Download,
  Trash2,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { textForGroup } from '../utils/groupColors';

const MAX_DOWNLOAD_LIMIT = 35;

const CratePanel = () => {
  const {
    crate,
    crateOpen,
    setCrateOpen,
    removeFromCrate,
    clearCrate
  } = usePreview();
  
  const [playingId, setPlayingId] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const audioRef = useRef(null);
  
  // Solo preview for crate items
  const toggleSoloPreview = (item) => {
    if (playingId === item.loop_id) {
      // Stop playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingId(null);
    } else {
      // Stop any existing audio
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      // Play new audio
      const previewUrl = getPreviewUrl(item);
      if (!previewUrl) {
        toast.error('No preview available');
        return;
      }
      audioRef.current = new Audio(previewUrl);
      audioRef.current.loop = true;
      audioRef.current.play();
      setPlayingId(item.loop_id);
      
      audioRef.current.onerror = () => {
        toast.error('Failed to play preview');
        setPlayingId(null);
      };
    }
  };
  
  // Download a single loop
  const downloadSingle = async (item) => {
    try {
      toast.info(`Downloading ${item.title}...`);
      const downloadUrl = getDownloadUrl(item);
      if (!downloadUrl) {
        toast.error('No download URL available');
        return;
      }
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Use appropriate extension based on item type
      const extension = item.isKnockEngine ? 'wav' : 'wav';
      a.download = `${item.title}.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${item.title}`);
    } catch (error) {
      console.error(`Failed to download ${item.title}:`, error);
      toast.error(`Failed to download ${item.title}`);
    }
  };
  
  // Download all as individual files
  const downloadAll = async () => {
    if (crate.length === 0) return;
    
    // Check download limit
    if (crate.length > MAX_DOWNLOAD_LIMIT) {
      toast.error(
        `Please download in smaller batches (max ${MAX_DOWNLOAD_LIMIT} loops) to keep things running smoothly. You can always come back for more.`,
        { duration: 6000 }
      );
      return;
    }
    
    setDownloading(true);
    toast.info(`Preparing ${crate.length} downloads...`);
    
    // Download each file individually
    let successCount = 0;
    for (const item of crate) {
      try {
        const downloadUrl = getDownloadUrl(item);
        if (!downloadUrl) continue;
        
        const response = await fetch(downloadUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const extension = item.isKnockEngine ? 'wav' : 'wav';
        a.download = `${item.title}.${extension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        successCount++;
        
        // Small delay between downloads
        await new Promise(r => setTimeout(r, 500));
      } catch (error) {
        console.error(`Failed to download ${item.title}:`, error);
      }
    }
    
    setDownloading(false);
    toast.success(`Downloaded ${successCount} loops`);
  };
  
  // Stop audio when panel closes
  React.useEffect(() => {
    if (!crateOpen && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlayingId(null);
    }
  }, [crateOpen]);
  
  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);
  
  // Instrument-group colours come from utils/groupColors. This file kept its own
  // copy with Drums orange and Textures blue, which contradicted the cards.
  // Generated Bass is not an instrument group, it is Knock Engine output, so it
  // keeps the orange Knock Engine treatment used elsewhere in the app.
  const groupColorClass = (group) =>
    group === 'Generated Bass'
      ? 'bg-orange-500/20 text-orange-400'
      : `bg-black/30 ${textForGroup(group)}`;
  
  // Get preview URL (handles both regular loops and Knock Engine items)
  const getPreviewUrl = (item) => {
    return item.previewUrl || item.preview_url;
  };
  
  // Get download URL (Knock Engine items use previewUrl for download)
  const getDownloadUrl = (item) => {
    return item.download_url || item.previewUrl;
  };
  
  // Get instrument group display
  const getInstrumentGroup = (item) => {
    return item.instrumentGroup || item.instrument_group || 'Loop';
  };
  
  return (
    <>
      {/* The floating Crate button sat on top of loop-card controls; the header
          Crate button opens this same panel and carries the count. */}
      
      {/* Crate Panel Overlay */}
      {crateOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setCrateOpen(false)}
          />
          
          {/* Panel */}
          <div className="relative bg-card border border-white/10 rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-accent" />
                <h2 className="font-heading text-lg font-bold">Download Crate</h2>
                {crate.length > 0 && (
                  <span className="bg-accent/20 text-accent text-xs font-bold px-2 py-0.5 rounded-full">
                    {crate.length} loop{crate.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <button
                onClick={() => setCrateOpen(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="overflow-y-auto p-4 space-y-2" style={{ maxHeight: 'calc(80vh - 140px)' }}>
              {crate.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Your crate is empty</p>
                  <p className="text-xs mt-1 opacity-70">Add loops to download them later</p>
                </div>
              ) : (
                crate.map((item) => (
                  <div
                    key={item.loop_id}
                    className={`flex items-center gap-3 rounded-xl p-3 border ${
                      item.isKnockEngine 
                        ? 'bg-gradient-to-r from-orange-500/10 to-amber-500/10 border-orange-500/20' 
                        : 'bg-muted/30 border-white/5'
                    }`}
                  >
                    {/* Solo Preview */}
                    <button
                      onClick={() => toggleSoloPreview(item)}
                      className={`p-2 rounded-lg transition-colors ${
                        playingId === item.loop_id
                          ? 'bg-primary text-black'
                          : 'bg-white/10 hover:bg-white/20'
                      }`}
                      title="Preview"
                    >
                      {playingId === item.loop_id ? (
                        <Square className="w-4 h-4" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </button>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${groupColorClass(getInstrumentGroup(item))}`}>
                          {getInstrumentGroup(item)}
                        </span>
                        <span className="text-xs text-muted-foreground">{item.bpm} BPM</span>
                        {item.key && <span className="text-xs text-muted-foreground">{item.key}</span>}
                      </div>
                    </div>
                    
                    {/* Download Single */}
                    <button
                      onClick={() => downloadSingle(item)}
                      className="p-2 hover:bg-accent/20 hover:text-accent rounded-lg transition-colors text-muted-foreground"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    
                    {/* Remove */}
                    <button
                      onClick={() => removeFromCrate(item.loop_id)}
                      className="p-2 hover:bg-red-500/20 hover:text-red-400 rounded-lg transition-colors text-muted-foreground"
                      title="Remove"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
            
            {/* Footer */}
            {crate.length > 0 && (
              <div className="p-4 border-t border-white/10">
                {/* Limit Warning */}
                {crate.length > MAX_DOWNLOAD_LIMIT && (
                  <div className="flex items-start gap-2 mb-3 text-sm text-yellow-500 bg-yellow-500/10 rounded-lg px-3 py-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Please download in smaller batches (max {MAX_DOWNLOAD_LIMIT}) to keep things running smoothly.</span>
                  </div>
                )}
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={clearCrate}
                    disabled={downloading}
                    className="rounded-full border-white/20"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                  <Button
                    onClick={downloadAll}
                    disabled={downloading || crate.length > MAX_DOWNLOAD_LIMIT}
                    className="flex-1 rounded-full bg-accent hover:bg-accent/90 disabled:opacity-50"
                    title="Download All Loops"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {downloading ? 'Downloading...' : `Download All (${crate.length})`}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CratePanel;
